<template>
  <router-link v-slot="{ navigate, href }" :to="detailsRoute" custom>
    <a
      :href="href"
      class="kanban-card"
      :class="{
        'kanban-card--navigating': isNavigating,
        'kanban-card--blocked': disabled,
      }"
      @click="onClick(navigate, $event)"
    >
      <div v-if="isNavigating" class="kanban-card__navigating-overlay">
        <spinner size="small" color-scheme="dark" />
      </div>

      <div class="kanban-card__row">
        <span class="kanban-card__name" :title="opportunity.name">
          {{ opportunity.name }}
        </span>
        <span class="kanban-card__date">
          {{
            $t('OPPORTUNITY_KANBAN.CARD.CREATED_AT', {
              date: formatDate(opportunity.created_at),
            })
          }}
        </span>
      </div>

      <div class="kanban-card__row">
        <span class="kanban-card__value">{{ formattedValue }}</span>
        <span
          class="kanban-card__status"
          :class="`kanban-card__status--${opportunity.status}`"
        >
          {{ statusLabel }}
        </span>
      </div>

      <div v-if="opportunity.contact" class="kanban-card__contact">
        <div class="kanban-card__contact-row">
          <span class="kanban-card__id">#{{ opportunity.id }}</span>
          <span
            class="kanban-card__contact-name"
            :title="opportunity.contact.name"
          >
            {{ opportunity.contact.name }}
          </span>
        </div>
        <div
          v-if="opportunity.contact.phone_number"
          class="kanban-card__contact-phone"
        >
          {{ opportunity.contact.phone_number }}
        </div>
      </div>

      <div class="kanban-card__footer">
        <div class="kanban-card__owner">
          <woot-thumbnail
            v-if="opportunity.owner"
            :src="opportunity.owner.thumbnail"
            :username="opportunity.owner.name"
            size="20px"
          />
          <div class="kanban-card__owner-text">
            <span class="kanban-card__owner-name" :title="ownerName">{{
              ownerName
            }}</span>
            <span
              v-if="opportunity.owner && opportunity.owner.email"
              class="kanban-card__owner-email"
            >
              {{ opportunity.owner.email }}
            </span>
          </div>
        </div>
        <div class="kanban-card__updated">
          {{
            $t('OPPORTUNITY_KANBAN.CARD.UPDATED_AT', {
              date: formatDate(opportunity.updated_at),
            })
          }}
          <span
            class="kanban-card__sla-dot"
            :class="`kanban-card__sla-dot--${opportunity.sla_status}`"
            :title="slaLabel"
          />
        </div>
      </div>
    </a>
  </router-link>
</template>

<script>
import Spinner from 'shared/components/Spinner';

export default {
  components: { Spinner },
  props: {
    opportunity: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNavigating: false,
    };
  },
  computed: {
    detailsRoute() {
      return {
        name: 'opportunities_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          opportunityId: this.opportunity.id,
        },
      };
    },
    formattedValue() {
      const amount = Number(this.opportunity.value || 0);
      return `${this.opportunity.currency || 'BRL'} ${amount.toLocaleString(
        'pt-BR',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;
    },
    ownerName() {
      return this.opportunity.owner ? this.opportunity.owner.name : '-';
    },
    statusLabel() {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.STATUS_VALUES.${this.opportunity.status}`
      );
    },
    slaLabel() {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.SLA_VALUES.${this.opportunity.sla_status}`
      );
    },
  },
  methods: {
    onClick(navigate, event) {
      if (this.disabled || this.isNavigating) {
        event.preventDefault();
        return;
      }

      this.isNavigating = true;
      this.$emit('navigate-start', this.opportunity.id);

      Promise.resolve(navigate(event)).catch(() => {
        this.isNavigating = false;
        this.$emit('navigate-cancel', this.opportunity.id);
      });
    },
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    },
  },
};
</script>

<style scoped lang="scss">
.kanban-card {
  background: var(--white);
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-small);
  color: inherit;
  display: block;
  margin-bottom: var(--space-small);
  padding: var(--space-normal);
  position: relative;

  &:hover {
    box-shadow: var(--shadow-medium);
  }
}

.kanban-card--navigating {
  cursor: wait;
  pointer-events: none;
}

.kanban-card--blocked {
  cursor: default;
  opacity: 0.5;
  pointer-events: none;
}

.kanban-card__navigating-overlay {
  align-items: center;
  background: var(--white);
  border-radius: var(--border-radius-medium);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: 0.8;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}

.kanban-card--dragging {
  box-shadow: var(--shadow-medium);
  cursor: grabbing;
  opacity: 0.4;
}

.kanban-card--ghost {
  background: var(--s-50);
  border: 1px dashed var(--s-200);
  box-shadow: none;
  color: transparent;

  * {
    visibility: hidden;
  }
}

.kanban-card__row {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.kanban-card__name {
  flex: 1;
  font-weight: var(--font-weight-medium);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-card__date,
.kanban-card__updated {
  color: var(--s-500);
  flex-shrink: 0;
  font-size: var(--font-size-mini);
  white-space: nowrap;
}

.kanban-card__value {
  font-weight: var(--font-weight-bold);
}

.kanban-card__status {
  border-radius: var(--border-radius-normal);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  padding: 0 var(--space-smaller);

  &--open {
    background: var(--y-100);
    color: var(--y-700);
  }
  &--won {
    background: var(--g-100);
    color: var(--g-700);
  }
  &--lost,
  &--canceled {
    background: var(--r-100);
    color: var(--r-700);
  }
}

.kanban-card__contact {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin-bottom: var(--space-normal);
}

.kanban-card__contact-row {
  display: flex;
  gap: var(--space-micro);
  min-width: 0;
}

.kanban-card__contact-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-card__contact-phone {
  color: var(--s-500);
}

.kanban-card__id {
  color: var(--w-500);
  flex-shrink: 0;
  font-weight: var(--font-weight-medium);
}

.kanban-card__footer {
  align-items: center;
  border-top: 1px solid var(--s-50);
  display: flex;
  justify-content: space-between;
  padding-top: var(--space-normal);
}

.kanban-card__owner {
  align-items: center;
  display: flex;
  gap: var(--space-small);
  min-width: 0;
}

.kanban-card__owner-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-micro);
  min-width: 0;
}

.kanban-card__owner-name {
  font-size: var(--font-size-mini);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-card__owner-email {
  color: var(--s-500);
  font-size: var(--font-size-micro);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-card__updated {
  align-items: center;
  display: flex;
  gap: var(--space-micro);
}

.kanban-card__sla-dot {
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;

  &--on_time {
    background: var(--g-500);
  }
  &--warning {
    background: var(--y-500);
  }
  &--overdue {
    background: var(--r-500);
  }
}
</style>
